import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { USER_ROLES } from '../../../utils/constants';
import {
  Target, Eye, Edit, Trash2, Plus,
  XCircle, CheckCircle, AlertTriangle, Calendar,
  User, TrendingUp, BarChart3, MoreVertical, ChevronDown, Check, Search, X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download
} from 'lucide-react';
import targetService from '../../../api/crm/targets/targetService';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import Button from '../../../components/common/Button/Button';
import { API_ENDPOINTS } from '../../../api/config';
import {
  handleTextInputChange,
  handleNumberInputChange,
  handleSearchInputChange,
  validateText,
  validateNumberInput,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS,
  NUMBER_LIMITS
} from '../../../utils/inputValidation';
import { capitalizeFirst } from '../../../utils/stringUtils';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV } from '../../../utils/exportUtils';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import BulkTargetUpload from './BulkTargetUpload';

const CreatedTargets = ({
  targets: propTargets,
  setTargets: propSetTargets,
  onExport
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userRole } = usePermissions();

  // Internal state for targets if not provided as prop
  const [internalTargets, setInternalTargets] = useState([]);

  // Use prop targets if provided, otherwise use internal state
  const targets = propTargets !== undefined ? propTargets : internalTargets;
  const setTargets = propSetTargets || setInternalTargets;

  // States
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalTargets, setTotalTargets] = useState(0);

  // Sorting state
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  // Modal States (View modal removed - now using dedicated page)
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState(null);
  const [editingTarget, setEditingTarget] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Export State
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Edit Form State
  const [editForm, setEditForm] = useState({
    name: '',
    type: 'revenue',
    period: 'monthly',
    scope: 'individual',
    value: '',
    assignee: '',
    assigneeName: '',
    assignees: [],
    assigneeNames: [],
    team: '',
    region: '',
    status: 'active',
    baselineRules: ''
  });
  const [errors, setErrors] = useState({});

  // CRM Users for Assignee dropdown
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [filteredTeamMembers, setFilteredTeamMembers] = useState([]);
  const MAX_VISIBLE_USERS = 20;
  const assigneeDropdownRef = useRef(null);
  const assigneeInputRef = useRef(null);

  const targetTypes = [
    { id: 'revenue', name: 'Revenue Target', description: 'Total sales revenue target' },
    { id: 'deals', name: 'Deal Count', description: 'Number of deals closed' },
    { id: 'newOpps', name: 'New Opportunities', description: 'New opportunities created' },
    { id: 'activities', name: 'Activities', description: 'Sales activities completed' }
  ];

  // Load targets from API with pagination
  const loadTargets = async () => {
    try {
      setLoading(true);

      const isCustom = Boolean(customRange.startDate && customRange.endDate);

      const filters = {
        period: filterPeriod !== 'all' ? filterPeriod : undefined,
        search: searchQuery.trim() || undefined,
        range: isCustom ? 'custom' : 'all_time',
        startDate: isCustom ? customRange.startDate : undefined,
        endDate: isCustom ? customRange.endDate : undefined,
        page: currentPage,
        limit: itemsPerPage
      };

      const result = await targetService.getAllTargets(filters);

      if (result.success) {
        const targetsData = Array.isArray(result.targets) ? result.targets : [];
        if (setTargets) {
          setTargets(targetsData);
        }
        // Set total count if available from API
        if (result.total !== undefined) {
          setTotalTargets(result.total);
        } else {
          setTotalTargets(targetsData.length);
        }
      } else {
        if (setTargets) {
          setTargets([]);
        }
        setTotalTargets(0);
      }
    } catch (error) {
      if (setTargets) {
        setTargets([]);
      }
      setTotalTargets(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch CRM team users for assignee dropdown
  const fetchCRMUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      // Add roleFilter=sales to fetch only Sales Manager and Sales Executive users
      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?roleFilter=sales`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        const crmUsers = result.data?.users || [];

        // Transform users to match our format
        const transformedUsers = crmUsers.map(user => ({
          id: user._id || user.id,
          name: user.name || 'Unknown',
          email: user.email || '',
          role: user.role || '',
          department: user.department || '',
          designation: user.designation || '',
          team: user.department || 'Sales Team'
        })).sort((a, b) => a.name.localeCompare(b.name));

        setTeamMembers(transformedUsers);
        setFilteredTeamMembers(transformedUsers);
      } else {
        setTeamMembers([]);
        setFilteredTeamMembers([]);
      }
    } catch (error) {
      setTeamMembers([]);
      setFilteredTeamMembers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  // Load CRM users on component mount
  useEffect(() => {
    fetchCRMUsers();
  }, [fetchCRMUsers]);

  // Filter team members based on search term
  useEffect(() => {
    if (assigneeSearchTerm.trim() === '') {
      setFilteredTeamMembers(teamMembers);
    } else {
      const searchLower = assigneeSearchTerm.toLowerCase().trim();
      const filtered = teamMembers.filter(member =>
        member.name.toLowerCase().includes(searchLower) ||
        (member.email && member.email.toLowerCase().includes(searchLower)) ||
        (member.role && member.role.toLowerCase().includes(searchLower)) ||
        (member.department && member.department.toLowerCase().includes(searchLower))
      );
      setFilteredTeamMembers(filtered);
    }
  }, [assigneeSearchTerm, teamMembers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        setIsAssigneeDropdownOpen(false);
        setAssigneeSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted targets
  const getSortedTargets = () => {
    if (!sortColumn || !targets || targets.length === 0) return targets;

    return [...targets].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'name') {
        aValue = (a.name || '').toLowerCase();
        bValue = (b.name || '').toLowerCase();
      } else if (sortColumn === 'type') {
        aValue = (a.type || '').toLowerCase();
        bValue = (b.type || '').toLowerCase();
      } else if (sortColumn === 'period') {
        aValue = (a.period || '').toLowerCase();
        bValue = (b.period || '').toLowerCase();
      } else if (sortColumn === 'scope') {
        aValue = (a.scope || '').toLowerCase();
        bValue = (b.scope || '').toLowerCase();
      } else if (sortColumn === 'value') {
        aValue = Number(a.value) || 0;
        bValue = Number(b.value) || 0;
      } else if (sortColumn === 'startDate') {
        aValue = new Date(a.startDate || 0).getTime();
        bValue = new Date(b.startDate || 0).getTime();
      } else if (sortColumn === 'status') {
        aValue = (a.computedStatus || a.status || '').toLowerCase();
        bValue = (b.computedStatus || b.status || '').toLowerCase();
      } else if (sortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      const order = sortDirection === 'asc' ? 1 : -1;
      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
      return 0;
    });
  };

  // Load targets on mount, when location changes, or when filters change
  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on filter change
      loadTargets();
    }, 300); // 300ms debounce for search

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterPeriod, customRange.startDate, customRange.endDate]);

  // Load targets when pagination changes
  useEffect(() => {
    loadTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage]);

  // Reset to page 1 when filters or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterPeriod, customRange.startDate, customRange.endDate, itemsPerPage]);

  // Format date helper function (12 Dec 2025 format)
  const formatDate = (dateString, options = {}) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });

      if (options.includeTime) {
        const timePart = date.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
        return `${datePart}, ${timePart}`;
      }

      return datePart;
    } catch (error) {
      return 'N/A';
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  // Reset selection when filters or pagination change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [currentPage, itemsPerPage, searchQuery, filterPeriod]);

  // ** Export Handlers **

  const handleSelectAll = (e) => {
    const data = getSortedTargets();
    if (e.target.checked) {
      const allIds = data.map(item => item.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const mapDataForExport = (data) => {
    return data.map(item => ({
      "Target Name": formatTargetName(item),
      "Type": targetTypes.find(t => t.id === item.type)?.name || item.type,
      "Period": capitalizeFirst(item.period),
      "Scope": capitalizeFirst(item.scope),
      "Value": item.value,
      "Start Date": formatDate(item.startDate),
      "End Date": formatDate(item.endDate),
      "Status": formatStatusLabel(item.status, item.computedStatus),
      "Created At": formatDate(item.createdAt),
      "Assignee": item.assigneeName || 'N/A',
      "Team": item.team || 'N/A'
    }));
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      const sourceData = getSortedTargets();
      const selectedData = sourceData.filter(item => selectedRows.has(item.id));
      const exportData = mapDataForExport(selectedData);

      exportToCSV(exportData, 'targets_export', {
        title: 'Sales Targets',
        type: 'Selected Items'
      });
      setSelectedRows(new Set());
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    const sourceData = getSortedTargets();
    const exportData = mapDataForExport(sourceData);

    exportToCSV(exportData, 'targets_export', {
      title: 'Sales Targets',
      type: 'Current Page'
    });
    setShowExportModal(false);
  };

  // Helper to fetch all data for export
  const fetchAllForExport = async (filters) => {
    const exportLimit = 1000;
    return await targetService.getAllTargets({ ...filters, limit: exportLimit, page: 1 });
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const isCustom = Boolean(customRange.startDate && customRange.endDate);

      const filters = {
        period: filterPeriod !== 'all' ? filterPeriod : undefined,
        search: searchQuery.trim() || undefined,
        range: isCustom ? 'custom' : 'all_time',
        startDate: startDate || (isCustom ? customRange.startDate : undefined),
        endDate: endDate || (isCustom ? customRange.endDate : undefined)
      };

      const result = await fetchAllForExport(filters);

      if (result.success) {
        const data = result.targets || [];
        const exportData = mapDataForExport(data);

        exportToCSV(exportData, 'targets_export', {
          title: 'Sales Targets',
          type: 'Date Range',
          dateRange: { startDate, endDate }
        });
        setShowExportModal(false);
      } else {
        alert('Failed to fetch data for export. Please try again.');
      }
    } catch (error) {
      alert('An error occurred during export.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      // Pass null dates to fetch all matching current filters
      const isCustom = Boolean(customRange.startDate && customRange.endDate);

      const filters = {
        period: filterPeriod !== 'all' ? filterPeriod : undefined,
        search: searchQuery.trim() || undefined,
        range: isCustom ? 'custom' : 'all_time',
        startDate: isCustom ? customRange.startDate : undefined,
        endDate: isCustom ? customRange.endDate : undefined
      };

      const result = await fetchAllForExport(filters);

      if (result.success) {
        const data = result.targets || [];
        const exportData = mapDataForExport(data);

        exportToCSV(exportData, 'targets_export', {
          title: 'Sales Targets',
          type: 'All Data'
        });
        setShowExportModal(false);
      } else {
        alert('Failed to fetch data for export. Please try again.');
      }
    } catch (error) {
      alert('An error occurred during export.');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalTargets / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalTargets);

  // View Target Handler - Navigate to target details page
  const handleViewTarget = (target) => {
    if (!target || !target.id) return;
    navigate(`/target/${target.id}`);
  };

  // Edit Target Handler - navigate to unified Target Builder page
  const handleEditTarget = (target) => {
    if (!target || !target.id) return;
    navigate(`/target_builder?id=${encodeURIComponent(target.id)}`);
  };

  // Update Target Handler
  const handleUpdateTarget = async (e) => {
    e.preventDefault();

    const isTeamScope = editForm.scope === 'team';

    // Validate
    const requiredFields = ['value'];
    const validation = validateRequiredFields(editForm, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    if (isTeamScope) {
      if (!editForm.assignees || editForm.assignees.length === 0) {
        setErrors({ assignee: 'Please select at least one team member' });
        return;
      }
    } else {
      if (!editForm.assignee) {
        setErrors({ assignee: 'Please select a valid assignee' });
        return;
      }
    }

    // Validate value
    const valueValidation = validateNumberInput(editForm.value, Infinity, 0);
    if (!valueValidation.isValid) {
      setErrors({ ...errors, value: valueValidation.error });
      return;
    }

    setErrors({});

    try {
      setLoading(true);

      const primaryAssigneeId = isTeamScope
        ? (editForm.assignees && editForm.assignees.length > 0 ? editForm.assignees[0] : '')
        : editForm.assignee;

      const selectedIds = isTeamScope ? (editForm.assignees || []) : [primaryAssigneeId];
      const selectedMembers = teamMembers.filter(m => selectedIds.includes(m.id));

      const updateData = {
        name: editForm.name || `${editForm.type} - ${editForm.period}`,
        type: editForm.type,
        period: editForm.period,
        scope: editForm.scope,
        value: parseFloat(editForm.value),
        assignee: primaryAssigneeId,
        assigneeName: selectedMembers[0]?.name || editForm.assigneeName,
        assignees: isTeamScope ? selectedIds : undefined,
        assigneeNames: isTeamScope ? selectedMembers.map(m => m.name) : undefined,
        team: selectedMembers[0]?.department || selectedMembers[0]?.team || editForm.team,
        region: editForm.region,
        status: editForm.status,
        baselineRules: editForm.baselineRules
      };

      const result = await targetService.updateTarget(
        editingTarget.id,
        updateData
      );

      if (result.success) {
        // Update local state
        if (setTargets) {
          setTargets(prev => prev.map(t =>
            t.id === editingTarget.id ? result.target : t
          ));
        }

        setShowEditModal(false);
        setEditingTarget(null);
        alert('Target updated successfully');
        loadTargets(); // Reload to ensure consistency
      } else {
        alert('Failed to update target: ' + result.error);
      }
    } catch (error) {
      alert('Error updating target. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete Target Handler - Open confirmation dialog
  const handleDeleteTarget = (targetId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setTargetToDelete(targetId);
    setShowDeleteConfirm(true);
  };

  // Confirm Delete Target
  const confirmDeleteTarget = async () => {
    if (!targetToDelete) return;

    try {
      setLoading(true);
      const result = await targetService.deleteTarget(targetToDelete);

      if (result.success) {
        // Remove from local state
        if (setTargets) {
          setTargets(prev => prev.filter(t => t.id !== targetToDelete));
        }
        alert('Target deleted successfully');
        loadTargets(); // Reload to ensure consistency
      } else {
        alert('Failed to delete target: ' + result.error);
      }
    } catch (error) {
      alert('Error deleting target. Please try again.');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setTargetToDelete(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      setLoading(true);
      const result = await targetService.bulkDeleteTargets(Array.from(selectedRows));

      if (result.success) {
        alert('Targets deleted successfully');
        setSelectedRows(new Set());
        loadTargets();
      } else {
        alert('Failed to delete targets: ' + result.error);
      }
    } catch (error) {
      alert('Error during bulk deletion. Please try again.');
    } finally {
      setLoading(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  // Cancel Delete
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setTargetToDelete(null);
  };

  // Bulk delete removed - not needed for consistent UI

  // Removed bulk selection functions - not needed for consistent UI


  // Get Status Color (uses computedStatus - system-driven, never manual)
  const getStatusColor = (status, computedStatus) => {
    // ALWAYS use computedStatus (system-driven), ignore legacy status field completely
    const effectiveStatus = (computedStatus || 'draft').toLowerCase();
    switch (effectiveStatus) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'active':
        return 'bg-brand/10 text-brand';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'incomplete':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format status label with first letter capital (e.g. "active" -> "Active")
  // ALWAYS use computedStatus (system-driven), ignore legacy status field
  const formatStatusLabel = (status, computedStatus) => {
    // computedStatus is the single source of truth - never use legacy status
    const safeStatus = computedStatus || 'draft';
    return capitalizeFirst(safeStatus.toString());
  };

  // Format target name - capitalize first letter of each word (Title Case)
  const formatTargetName = (target) => {
    const name = target.name || `${target.type} - ${target.period}`;
    // Capitalize first letter of each word
    return name.replace(/\b\w/g, char => char.toUpperCase());
  };

  // Get Type Badge Color
  const getTypeColor = (type) => {
    switch (type) {
      case 'revenue':
        return 'bg-brand/10 text-brand';
      case 'deals':
        return 'bg-purple-100 text-purple-800';
      case 'newOpps':
        return 'bg-green-100 text-green-800';
      case 'activities':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Display targets directly (filtering and sorting handled by API)
  const displayTargets = targets || [];

  return (
    <div className="p-4 sm:p-6 font-sans space-y-6">
        {/* Header Container */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
                <Target className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Target Management</h1>
                <p className="text-sm sm:text-base text-slate-500 mt-1">Create, manage, and track all your sales targets and their performance</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {selectedRows.size > 0 && (
                <PermissionGate permission="target_achievement" action="delete">
                  <button
                    onClick={handleBulkDelete}
                    disabled={loading}
                    className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Delete ({selectedRows.size})</span>
                  </button>
                </PermissionGate>
              )}

              <PermissionGate permission="target_achievement" action="read">
                <button
                  onClick={handleExportClick}
                  className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || displayTargets.length === 0}
                >
                  <Download className="h-4 w-4" />
                  <span>{selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}</span>
                </button>
              </PermissionGate>

              {userRole !== USER_ROLES.SALES_EXECUTIVE && (
                <PermissionGate permission="target_achievement" action="create">
                  <button
                    onClick={() => setShowBulkUpload(true)}
                    className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
                  >
                    <Download className="h-4 w-4 rotate-180" />
                    <span>Bulk Upload</span>
                  </button>
                </PermissionGate>
              )}

              {userRole !== USER_ROLES.SALES_EXECUTIVE && (
                <PermissionGate permission="target_achievement" action="create">
                  <button
                    onClick={() => navigate('/target_builder')}
                    className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Create Target</span>
                  </button>
                </PermissionGate>
              )}
            </div>
          </div>
        </div>

        {/* Search and Period Filter */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by target name, assignee, team..."
                value={searchQuery}
                onChange={(e) => {
                  const sanitized = handleSearchInputChange(e, setSearchQuery, FIELD_LIMITS.search);
                  setSearchQuery(sanitized);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                className="input-field pl-10 pr-10 py-2 w-full"
                autoComplete="off"
                maxLength={FIELD_LIMITS.search}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Period Filter */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center lg:flex-shrink-0">
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                className="input-field w-full sm:w-auto px-3 shadow-sm"
              >
                <option value="all">All Plan Periods</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>

              <DateRangeFilter
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={(newRange) => setCustomRange((prev) => ({ ...prev, ...newRange }))}
                allowFutureDates={false}
              />

              {(customRange.startDate || customRange.endDate) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setCustomRange({ startDate: '', endDate: '' });
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Targets Table */}
        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {Array.from({ length: 9 }).map((_, index) => (
                      <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.from({ length: 5 }).map((_, rowIndex) => (
                    <tr key={rowIndex} className="animate-pulse">
                      {Array.from({ length: 9 }).map((_, colIndex) => (
                        <td key={colIndex} className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-full"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : displayTargets.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                        checked={getSortedTargets().length > 0 && getSortedTargets().every(item => selectedRows.has(item.id))}
                        onChange={(e) => {
                          const currentData = getSortedTargets();
                          if (e.target.checked) {
                            const newSelected = new Set(selectedRows);
                            currentData.forEach(item => newSelected.add(item.id));
                            setSelectedRows(newSelected);
                          } else {
                            const newSelected = new Set(selectedRows);
                            currentData.forEach(item => newSelected.delete(item.id));
                            setSelectedRows(newSelected);
                          }
                        }}
                        disabled={getSortedTargets().length === 0}
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Target Name</span>
                        {sortColumn === 'name' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('type')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Type</span>
                        {sortColumn === 'type' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('period')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Period</span>
                        {sortColumn === 'period' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('scope')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Scope</span>
                        {sortColumn === 'scope' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('value')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Value</span>
                        {sortColumn === 'value' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('startDate')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Period Range</span>
                        {sortColumn === 'startDate' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Status</span>
                        {sortColumn === 'status' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Created</span>
                        {sortColumn === 'createdAt' ? (
                          sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                        ) : (
                          <ArrowUpDown className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getSortedTargets().map((target) => (
                    <tr key={target.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                          checked={selectedRows.has(target.id)}
                          onChange={() => handleSelectRow(target.id)}
                        />
                      </td>
                      <td className="px-6 py-4 max-w-xs">
                        <div
                          className="text-sm font-medium text-gray-900 truncate"
                          title={formatTargetName(target)}
                        >
                          {formatTargetName(target)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(target.type)}`}>
                          {targetTypes.find(t => t.id === target.type)?.name || target.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {target.period}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {target.scope}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {target.type === 'revenue' || target.type === 'activities'
                          ? `₹${formatIndianAmount(target.value || 0)}`
                          : target.value?.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {target.startDate && target.endDate
                          ? `${formatDate(target.startDate)} – ${formatDate(target.endDate)}`
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(target.status, target.computedStatus)}`}>
                          {formatStatusLabel(target.status, target.computedStatus)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(target.createdAt, { includeTime: true })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <PermissionGate permission="target_achievement" action="read">
                            <button
                              onClick={() => handleViewTarget(target)}
                              className="text-brand hover:underline"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="target_achievement" action="update">
                            <button
                              onClick={() => handleEditTarget(target)}
                              className={`${(target.computedStatus && target.computedStatus.toLowerCase() === 'completed')
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-green-600 hover:text-green-900'
                                }`}
                              disabled={target.computedStatus && target.computedStatus.toLowerCase() === 'completed'}
                              title="Edit Target"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="target_achievement" action="delete">
                            <button
                              type="button"
                              onClick={(e) => handleDeleteTarget(target.id, e)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Target"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Targets Found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || filterPeriod !== 'all'
                ? 'No targets match your search criteria'
                : 'Create your first target to start tracking performance'}
            </p>
            {!searchQuery && filterPeriod === 'all' && userRole !== USER_ROLES.SALES_EXECUTIVE && (
              <PermissionGate permission="target_achievement" action="create">
                <button
                  onClick={() => navigate('/target_builder')}
                  className="btn-create px-6 py-2"
                >
                  Create Target
                </button>
              </PermissionGate>
            )}
          </div>
        )}

        {/* Pagination - Matching Lead List style */}
        {(() => {
          const shouldShowPagination = !loading && totalPages > 1;

          return shouldShowPagination ? (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                {/* Results Count and Limit Selector */}
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700">
                    Showing {startIndex + 1} to {endIndex} of {totalTargets} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Items per page:</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                    >
                      <option value={3}>3</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {(() => {
                      const pages = [];

                      if (totalPages <= 10) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);

                        if (currentPage <= 4) {
                          for (let i = 2; i <= 5; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        } else if (currentPage >= totalPages - 3) {
                          pages.push('ellipsis');
                          for (let i = totalPages - 4; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push('ellipsis');
                          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        }
                      }

                      return pages.map((page, index) => {
                        if (page === 'ellipsis') {
                          return (
                            <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }

                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPage
                              ? 'bg-brand text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      });
                    })()}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : null;
        })()}

        {/* Edit Target Modal */}
        {showEditModal && editingTarget && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="px-5 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Edit Target</h3>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTarget(null);
                      setErrors({});
                      setIsAssigneeDropdownOpen(false);
                      setAssigneeSearchTerm('');
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <form className="p-5 space-y-4" onSubmit={handleUpdateTarget}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Target Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Target Name
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => {
                        handleTextInputChange(e, (value) => {
                          // Auto-capitalize first letter of target name
                          const formatted = capitalizeFirst(value);
                          setEditForm(prev => ({ ...prev, name: formatted }));
                        }, FIELD_LIMITS.customerName);
                        if (errors.name) {
                          setErrors(prev => ({ ...prev, name: '' }));
                        }
                      }}
                      className={`w-full border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent`}
                      placeholder="Enter target name"
                    />
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-500">
                        {getCharacterCount(editForm.name, FIELD_LIMITS.customerName)}
                      </span>
                      {errors.name && (
                        <span className="text-red-500 text-xs">{errors.name}</span>
                      )}
                    </div>
                  </div>

                  {/* Target Type */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Target Type
                    </label>
                    <select
                      value={editForm.type}
                      onChange={(e) => setEditForm(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    >
                      {targetTypes.map((type) => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Period */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Period
                    </label>
                    <select
                      value={editForm.period}
                      onChange={(e) => setEditForm(prev => ({ ...prev, period: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>

                  {/* Scope */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Scope
                    </label>
                    <select
                      value={editForm.scope}
                      onChange={(e) => setEditForm(prev => ({
                        ...prev,
                        scope: e.target.value,
                        assignee: '',
                        assignees: []
                      }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    >
                      <option value="individual">Individual</option>
                      <option value="team">Team</option>
                    </select>
                  </div>

                  {/* Target Value */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Target Value *
                    </label>
                    <input
                      type="number"
                      value={editForm.value}
                      onChange={(e) => {
                        handleNumberInputChange(e, (value) => {
                          setEditForm(prev => ({ ...prev, value: value }));
                        }, Infinity, 0);
                        if (errors.value) {
                          setErrors(prev => ({ ...prev, value: '' }));
                        }
                      }}
                      className={`w-full border ${errors.value ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent`}
                      placeholder="Enter target value"
                      required
                    />
                    {errors.value && (
                      <p className="text-red-500 text-xs mt-1">{errors.value}</p>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Status
                    </label>
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="completed">Completed</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>

                  {/* Assignee(s) */}
                  <div className="relative" ref={assigneeDropdownRef}>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      {editForm.scope === 'team' ? 'Assignees *' : 'Assignee *'}
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setIsAssigneeDropdownOpen(!isAssigneeDropdownOpen);
                          if (!isAssigneeDropdownOpen) {
                            setTimeout(() => {
                              assigneeInputRef.current?.focus();
                            }, 100);
                          }
                        }}
                        className={`w-full text-left px-3 py-2 border ${errors.assignee ? 'border-red-500' : 'border-gray-300'
                          } rounded-md bg-white flex items-center justify-between ${loadingUsers ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'
                          } text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent`}
                        disabled={loadingUsers}
                      >
                        <span className={(editForm.assignee || (editForm.assignees && editForm.assignees.length > 0)) ? 'text-gray-900' : 'text-gray-500'}>
                          {editForm.scope === 'team'
                            ? (editForm.assignees && editForm.assignees.length > 0
                              ? `${editForm.assignees.length} member(s) selected`
                              : (loadingUsers ? 'Loading users...' : 'Select team members...'))
                            : (editForm.assignee
                              ? teamMembers.find(m => m.id === editForm.assignee)?.name || 'Select assignee...'
                              : (loadingUsers ? 'Loading users...' : 'Select assignee...'))}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isAssigneeDropdownOpen ? 'transform rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown */}
                      {isAssigneeDropdownOpen && (
                        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                          <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                ref={assigneeInputRef}
                                type="text"
                                value={assigneeSearchTerm}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                  setAssigneeSearchTerm(value);
                                }}
                                placeholder="Search users..."
                                maxLength={100}
                                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-[240px]">
                            {loadingUsers && filteredTeamMembers.length === 0 ? (
                              <div className="p-3 text-sm text-gray-500 text-center">
                                Loading CRM team members...
                              </div>
                            ) : filteredTeamMembers.length === 0 ? (
                              <div className="p-3 text-sm text-gray-500 text-center">
                                {assigneeSearchTerm ? 'No users found' : 'No CRM team members available'}
                              </div>
                            ) : (
                              (assigneeSearchTerm.trim() === ''
                                ? filteredTeamMembers.slice(0, MAX_VISIBLE_USERS)
                                : filteredTeamMembers
                              ).map((member) => {
                                const isSelectedIndividual = editForm.assignee === member.id;
                                const isSelectedTeam = editForm.assignees?.includes(member.id);
                                const isSelected = editForm.scope === 'team' ? isSelectedTeam : isSelectedIndividual;

                                return (
                                  <button
                                    key={member.id}
                                    type="button"
                                    onClick={() => {
                                      if (editForm.scope === 'team') {
                                        setEditForm(prev => {
                                          const current = prev.assignees || [];
                                          const exists = current.includes(member.id);
                                          const nextAssignees = exists
                                            ? current.filter(id => id !== member.id)
                                            : [...current, member.id];
                                          return { ...prev, assignees: nextAssignees };
                                        });
                                        if (errors.assignee) {
                                          setErrors(prev => ({ ...prev, assignee: '' }));
                                        }
                                      } else {
                                        setEditForm(prev => ({
                                          ...prev,
                                          assignee: member.id,
                                          assigneeName: member.name || '',
                                          team: member.department || member.team || '',
                                          region: member.region || 'North'
                                        }));
                                        setIsAssigneeDropdownOpen(false);
                                        setAssigneeSearchTerm('');
                                        if (errors.assignee) {
                                          setErrors(prev => ({ ...prev, assignee: '' }));
                                        }
                                      }
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${isSelected ? 'bg-brand/5 font-medium' : 'text-gray-700'
                                      }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <div className="font-medium">{member.name}</div>
                                        <div className="text-xs text-gray-500">
                                          {member.email} {member.designation ? `• ${member.designation}` : (member.department ? `• ${member.department}` : '')}
                                        </div>
                                      </div>
                                      {editForm.scope === 'team' ? (
                                        <input
                                          type="checkbox"
                                          readOnly
                                          checked={isSelectedTeam}
                                          className="h-4 w-4 text-brand border-gray-300 rounded"
                                        />
                                      ) : (
                                        isSelectedIndividual && (
                                          <Check className="h-4 w-4 text-brand" />
                                        )
                                      )}
                                    </div>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {errors.assignee && (
                      <p className="text-red-500 text-xs mt-1">{errors.assignee}</p>
                    )}
                    {!loadingUsers && teamMembers.length === 0 && !isAssigneeDropdownOpen && (
                      <p className="text-xs text-yellow-600 mt-1">No CRM team members found</p>
                    )}
                  </div>

                  {/* Team */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Team
                    </label>
                    <input
                      type="text"
                      value={editForm.team}
                      onChange={(e) => {
                        handleTextInputChange(e, (value) => {
                          // Auto-capitalize first letter of team name
                          const formatted = capitalizeFirst(value);
                          setEditForm(prev => ({ ...prev, team: formatted }));
                        }, FIELD_LIMITS.customerName);
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                      placeholder="Enter team name"
                    />
                  </div>

                  {/* Region */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      Region
                    </label>
                    <select
                      value={editForm.region}
                      onChange={(e) => setEditForm(prev => ({ ...prev, region: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    >
                      <option value="">Select region...</option>
                      <option value="North">North</option>
                      <option value="South">South</option>
                      <option value="East">East</option>
                      <option value="West">West</option>
                    </select>
                  </div>
                </div>

                {/* Baseline Rules */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Baseline Rules
                  </label>
                  <textarea
                    value={editForm.baselineRules}
                    onChange={(e) => {
                      handleTextInputChange(e, (value) => {
                        setEditForm(prev => ({ ...prev, baselineRules: value }));
                      }, FIELD_LIMITS.notes);
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                    rows={3}
                    placeholder="Enter baseline calculation rules and carry-over policies..."
                  ></textarea>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(editForm.baselineRules, FIELD_LIMITS.notes)}
                    </span>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex items-center justify-end space-x-3 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTarget(null);
                      setErrors({});
                      setIsAssigneeDropdownOpen(false);
                      setAssigneeSearchTerm('');
                    }}
                    className="btn-cancel px-4 py-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-brand text-white rounded-md hover:bg-brand/90 disabled:opacity-50 transition-colors text-sm font-medium shadow-sm"
                  >
                    {loading ? 'Updating...' : 'Update Target'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Delete Target
                </h3>
                <p className="text-sm text-gray-600 text-center mb-6">
                  Are you sure you want to delete this target? This action cannot be undone.
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={cancelDelete}
                    disabled={loading}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDeleteTarget}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    {loading ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExportCurrentPage={handleExportCurrentPage}
          onExportRange={handleExportRange}
          onExportAll={handleExportAll}
          loading={exportLoading}
          title="Export Targets"
          entityName="targets"
        />

        <ConfirmationDialog
          isOpen={showBulkDeleteConfirm}
          onClose={() => setShowBulkDeleteConfirm(false)}
          onConfirm={confirmBulkDelete}
          title="Confirm Bulk Delete"
          message={`Are you sure you want to delete ${selectedRows.size} selected target(s)? This action cannot be undone.`}
          confirmText="Delete All"
          type="danger"
        />

        <BulkTargetUpload
          isOpen={showBulkUpload}
          onComplete={() => {
            setShowBulkUpload(false);
            loadTargets();
          }}
          onCancel={() => setShowBulkUpload(false)}
        />
    </div>
  );
};

export default CreatedTargets;

